import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { finalize, forkJoin } from 'rxjs';

import { ViewPersistenceService } from 'src/app/features/lifecycle-command/services/view-persistence.service';
import { ReactiveFiltersModule } from '../../../reactive-filters/reactive-filters.module';
import { IFormControlConfig } from '../../../reactive-forms-ui/interfaces/form-control-config';
import { DevicesInCycleCardComponent } from './components/devices-in-cycle-card/devices-in-cycle-card.component';
import { DeploymentStatusCardComponent } from './components/deployment-status-card/deployment-status-card.component';
import { PatchesByTypeBarsComponent } from './components/patches-by-type-bars/patches-by-type-bars.component';
import { SeverityComplianceBarsComponent } from './components/severity-compliance-bars/severity-compliance-bars.component';
import { DeviceComplianceBarsComponent } from './components/device-compliance-bars/device-compliance-bars.component';
import { PatchDetailsTableComponent } from './components/patch-details-table/patch-details-table.component';
import { PatchManagementChartsModule } from './patch-management-charts.module';
import {
  createPatchFilterConfigMap,
  createPatchFiltersForm,
  applyPatchFilterOptions,
  extractPatchFilters,
  fromDateString,
  DEFAULT_DATA_SOURCE,
  DEFAULT_SEVERITY_FILTER,
  PATCH_FILTERS_LAYOUT,
} from './patch-management-filters.utils';

import { PatchManagementService } from '../../services/patch-management.service';
import {
  DeploymentStatusBreakdown,
  DeviceBreakdown,
  FilterOptions,
  HistoricPoint,
  HistoricStats,
  PatchRow,
  PatchSummary,
  PatchTypeBreakdown,
  SeverityBreakdown,
} from '../../models/patch-management.models';

export const KPI_PATCH_TYPES = [
  'Security Updates',
  'Third Party Updates',
  'Non Security Updates',
  'Driver',
  'Definition Update',
];

type ToggleableFilterKey = 'status' | 'patchType' | 'severity' | 'deviceType' | 'deploymentStatus';

@Component({
  selector: 'app-patch-management',
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    MatIconModule,
    NgxSkeletonLoaderModule,
    ReactiveFiltersModule,
    PatchManagementChartsModule,
    DevicesInCycleCardComponent,
    DeploymentStatusCardComponent,
    PatchesByTypeBarsComponent,
    SeverityComplianceBarsComponent,
    DeviceComplianceBarsComponent,
    PatchDetailsTableComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './patch-management.component.html',
  styleUrls: ['./patch-management.component.scss'],
})
export class PatchManagementComponent implements OnInit {
  loading = true;
  error = '';
  detailsError = '';

  summary: PatchSummary | null = null;
  patchTypes: PatchTypeBreakdown[] = [];
  severities: SeverityBreakdown[] = [];
  devices: DeviceBreakdown[] = [];
  deploymentStatuses: DeploymentStatusBreakdown[] = [];
  patches: PatchRow[] = [];

  filtersForm = createPatchFiltersForm();
  valuesMap: any = this.filtersForm.value;
  filtersConfigMap: Map<string, IFormControlConfig> = createPatchFilterConfigMap();
  readonly filtersLayoutConfig = PATCH_FILTERS_LAYOUT;
  private lastFilterOptions: FilterOptions | null = null;
  private readonly STORAGE_KEY = 'patch_management';

  selectedDevice: string | null = null;

  historyLoading = false;
  historyError = '';
  historicTrend: HistoricPoint[] = [];
  historicStats: HistoricStats | null = null;
  private historyLoaded = false;

  historicOn = signal(false);

  constructor(
    private patchManagementService: PatchManagementService,
    private cdr: ChangeDetectorRef,
    private viewPersistenceService: ViewPersistenceService,
  ) {}

  ngOnInit(): void {
    this.restoreFilters();
    this.loadFilterOptions();
  }

  /** Matches the ngx-skeleton-loader theme convention used elsewhere in this module
   * (e.g. dashboard-summaries, dashboard-thirty-days-chart) — same background token, one
   * shape per real card/chart it's standing in for. */
  skeletonTheme(height: number, radius: number): Record<string, string> {
    return {
      width: '100%',
      height: `${height}px`,
      'border-radius': `${radius}px`,
      background: 'var(--skeleton-background)',
      margin: '0',
    };
  }

  toggleHistoric(): void {
    const next = !this.historicOn();
    this.historicOn.set(next);
    if (next && !this.historyLoaded) {
      this.loadHistory();
    }
  }

  updateValuesMap(valuesMap: any): void {
    this.valuesMap = valuesMap;
  }

  applyFilters(): void {
    this.saveFilters();
    this.loadLiveView();
    this.refreshHistoryIfActive();
  }

  clearFilters(): void {
    const latestDate = this.lastFilterOptions?.snapshotDates?.length
      ? fromDateString(this.lastFilterOptions.snapshotDates[0])
      : null;
    this.filtersForm.reset({
      dataSource: [DEFAULT_DATA_SOURCE],
      date: latestDate,
      computer: null,
      patchType: null,
      vendor: null,
      status: null,
      severity: DEFAULT_SEVERITY_FILTER,
      deviceType: null,
      deploymentStatus: null,
    });
    this.valuesMap = this.filtersForm.value;
    this.viewPersistenceService.clearFilters(this.STORAGE_KEY);
    this.loadLiveView();
    this.refreshHistoryIfActive();
  }

  activeValue(key: ToggleableFilterKey): string | null {
    const current: string[] | null = this.filtersForm.value[key];
    return current?.length === 1 ? current[0] : null;
  }

  toggleFilter(key: ToggleableFilterKey, value: string): void {
    const isActive = this.activeValue(key) === value;
    this.filtersForm.patchValue({ [key]: isActive ? null : [value] });
    this.valuesMap = this.filtersForm.value;
    this.saveFilters();
    this.loadLiveView();
    this.refreshHistoryIfActive();
  }

  onDeviceSelect(device: string | null): void {
    this.selectedDevice = device;
    this.cdr.markForCheck();
  }

  private loadLiveView(): void {
    this.loading = true;
    this.error = '';
    this.selectedDevice = null;
    const filters = extractPatchFilters(this.valuesMap);
    forkJoin({
      summary: this.patchManagementService.getSummary(filters),
      details: this.patchManagementService.getDetails(filters),
    })
      .pipe(
        finalize(() => {
          this.loading = false;
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: ({ summary, details }) => {
          this.summary = summary.summary;
          this.patchTypes = KPI_PATCH_TYPES.filter((name) => summary.patchTypes.some((t) => t.name === name)).map(
            (name) => summary.patchTypes.find((t) => t.name === name)!,
          );
          this.severities = summary.severities;
          this.devices = summary.devices;
          this.deploymentStatuses = summary.deploymentStatuses;
          this.patches = details.patches;
        },
        error: () => {
          this.error = 'Failed to load Patch Management data.';
        },
      });
  }

  private saveFilters(): void {
    const date = this.valuesMap?.date as Date | null;
    this.viewPersistenceService.saveFilters(this.STORAGE_KEY, {
      ...this.valuesMap,
      date: date?.toISOString?.() ?? null,
    });
  }

  private restoreFilters(): void {
    const saved = this.viewPersistenceService.loadFilters(this.STORAGE_KEY);
    if (!saved) return;
    const rawDate = saved['date'] as string | null;
    const asArray = (v: unknown) => (Array.isArray(v) && v.length > 0 ? v : null);
    const restored = {
      dataSource: [DEFAULT_DATA_SOURCE],
      date: rawDate ? new Date(rawDate) : null,
      computer: asArray(saved['computer']),
      patchType: asArray(saved['patchType']),
      vendor: asArray(saved['vendor']),
      status: asArray(saved['status']),
      severity: Array.isArray(saved['severity']) ? saved['severity'] : DEFAULT_SEVERITY_FILTER,
      deviceType: asArray(saved['deviceType']),
      deploymentStatus: asArray(saved['deploymentStatus']),
    };
    this.filtersForm.patchValue(restored);
    this.valuesMap = restored;
  }

  private loadFilterOptions(): void {
    this.patchManagementService.getFilterOptions().subscribe({
      next: (options) => {
        this.lastFilterOptions = options;
        this.filtersConfigMap = applyPatchFilterOptions(options, this.filtersConfigMap);
        if (!this.filtersForm.value.date && options.snapshotDates?.length) {
          this.filtersForm.patchValue({ date: fromDateString(options.snapshotDates[0]) });
          this.valuesMap = this.filtersForm.value;
        }
        this.cdr.markForCheck();
        this.loadLiveView();
      },
      error: () => this.loadLiveView(),
    });
  }

  private refreshHistoryIfActive(): void {
    if (this.historicOn()) {
      this.loadHistory();
    }
  }

  private loadHistory(): void {
    this.historyLoading = true;
    this.historyError = '';
    const filters = extractPatchFilters(this.valuesMap);
    this.patchManagementService
      .getHistory(filters)
      .pipe(
        finalize(() => {
          this.historyLoading = false;
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: (history) => {
          this.historicTrend = history.trend;
          this.historicStats = history.stats;
          this.historyLoaded = true;
        },
        error: () => {
          this.historyError = 'Failed to load historic trend data.';
        },
      });
  }
}
